import React, { FC, Fragment, FocusEventHandler } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { useQuery } from '@apollo/client';
import { getChildClinicsWithParentTemplate } from 'apollo/templates/user/query.graphql';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { ClinicDropdown } from 'models/user';
import { useTranslation } from 'react-i18next';

type ChildClinicsAutocompleteProps = {
    onChildClinicChange: (clinic: ClinicDropdown | null) => void;
    /** Nothing is fetched or shown until a parent clinic is selected. */
    parentClinic?: ClinicDropdown | null;
    value?: ClinicDropdown | null;
    name?: string;
    onBlur?: FocusEventHandler<HTMLInputElement>;
    helperText?: string | boolean;
    error?: boolean;
    fullWidth?: boolean;
    useInForm?: boolean;
    required?: boolean;
};
const ChildClinicsAutocomplete: FC<ChildClinicsAutocompleteProps> = ({
    onChildClinicChange,
    parentClinic,
    value,
    name,
    onBlur,
    helperText,
    error,
    fullWidth,
    useInForm,
    required,
}: ChildClinicsAutocompleteProps) => {
    const { t: translate } = useTranslation('users');
    const { showBoundary } = useDenthubErrorBoundary();
    const { data: { childClinicsWithParent } = {}, loading: childClinicsLoading } = useQuery(
        getChildClinicsWithParentTemplate,
        {
            notifyOnNetworkStatusChange: true,
            skip: !parentClinic?._id,
            variables: {
                parentClinicId: parentClinic?._id,
                showOnlyDeliveryLocations: true,
            },
            onCompleted({ childClinicsWithParent }) {
                if (childClinicsWithParent.length === 1) {
                    onChildClinicChange(childClinicsWithParent[0]);
                }
            },
            onError(error) {
                showBoundary(error);
            },
        },
    );

    return (
        <Fragment>
            {childClinicsLoading || !childClinicsWithParent || childClinicsWithParent.length <= 1 ? null : (
                <Autocomplete
                    fullWidth={fullWidth}
                    id="select-default-clinic"
                    value={value || null}
                    options={childClinicsWithParent}
                    isOptionEqualToValue={(option: ClinicDropdown, value: ClinicDropdown) => option?._id === value?._id}
                    getOptionLabel={(option: ClinicDropdown) => option.name || ''}
                    renderOption={(props, option: ClinicDropdown) => (
                        <li {...{ ...props, key: option?._id }}>{option?.name || ''}</li>
                    )}
                    onChange={(_event, value) => {
                        onChildClinicChange(value);
                    }}
                    renderInput={(params) => (
                        <TextField
                            sx={{ minWidth: useInForm ? 0 : 300 }}
                            {...params}
                            id="select-default-clinic"
                            label={translate('users:DEFAULT_CLINIC')}
                            required={required}
                            name={name}
                            onBlur={onBlur}
                            helperText={helperText}
                            error={error}
                        />
                    )}
                />
            )}
        </Fragment>
    );
};

export default ChildClinicsAutocomplete;
